import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { rememberWarehouseAccount, savedWarehouseAccount } from '../companyPreference';
import { warehouseWrites } from '../updates/activity';
export const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';
interface User { id: string; name: string; email: string; role?: string }
interface Shop { id: string; domain: string; name: string; organizationId?: string }
interface AuthContextType {
  user: User | null; isAuthenticated: boolean; loading: boolean; shops: Shop[]; currentShop: Shop | null;
  login: (email: string, password: string) => Promise<boolean>; logout: () => Promise<void>;
  selectShop: (id: string) => void; refresh: () => Promise<boolean>;
}
const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [currentShop, setCurrentShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    const endWrite = warehouseWrites.begin();
    try {
      let response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (response.status === 401) {
        const renewed = await fetch(`${API_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (renewed.ok) response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      }
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Session expired');
      setUser(data.user); setShops(data.shops || []);
      const selected = data.shops?.find((s: Shop) => s.id === savedWarehouseAccount()) || data.shops?.find((s: Shop) => s.id === data.currentShop?.id) || data.shops?.[0] || null;
      setCurrentShop(selected); rememberWarehouseAccount(selected?.id || null); return true;
    } catch { setUser(null); setShops([]); setCurrentShop(null); return false; } finally { endWrite(); }
  }, []);
  useEffect(() => {
    try { localStorage.removeItem('warehouse_token'); localStorage.removeItem('warehouse_user'); } catch { /* Native cookies remain authoritative when browser storage is disabled. */ }
    void refresh().finally(() => setLoading(false));
  }, [refresh]);
  const login = async (email: string, password: string) => {
    const endWrite = warehouseWrites.begin();
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await response.json();
      return response.ok && data.success ? refresh() : false;
    } catch { return false; } finally { endWrite(); }
  };
  const logout = async () => {
    const endWrite = warehouseWrites.begin();
    try {
      const response = await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error('Sign out failed. Please try again.');
      setUser(null); setShops([]); setCurrentShop(null); rememberWarehouseAccount(null);
    } finally { endWrite(); }
  };
  const selectShop = (id: string) => {
    const shop = shops.find(s => s.id === id);
    if (shop) { rememberWarehouseAccount(id); setCurrentShop(shop); }
  };
  return <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, shops, currentShop, login, logout, selectShop, refresh }}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
